import { LEAVES_BG_XS, LEAVES_BG_MD } from '../../config/backgrounds';
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Button,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Container,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Add as AddIcon,
  Event as EventIcon,
  People as PeopleIcon,
  AccountBalanceWallet as MoneyIcon,
  CheckCircle as CheckIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Favorite as FavoriteIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { format } from 'date-fns';
import { hu } from 'date-fns/locale';
import weddingEventService from '../../services/weddingEventService';
import { formatHufCompact } from '../../utils/currency';
import CreateWeddingEventDialog from '../../components/WeddingPlanner/CreateWeddingEventDialog';
import EditWeddingEventDialog from '../../components/WeddingPlanner/EditWeddingEventDialog';

function WeddingPlannerDashboard() {
  const navigate = useNavigate();
  const location = useLocation();

  // A bemutató oldal „Saját esküvő létrehozása” gombja ide navigál, és rögtön nyitja a létrehozó ablakot
  useEffect(() => {
    if (location.state?.openCreate) {
      setCreateDialogOpen(true);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.state]); // eslint-disable-line react-hooks/exhaustive-deps
  const theme = useTheme();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);

  useEffect(() => {
    fetchEvents();
  }, []);

  const [loadingDemo, setLoadingDemo] = useState(false);
  const handleLoadDemo = async () => {
    setLoadingDemo(true);
    try {
      const res = await weddingEventService.resetDemo();
      if (res?.data?.id) navigate(`/wedding-planner/${res.data.id}`);
    } catch (err) {
      console.error('Load demo error:', err);
    } finally {
      setLoadingDemo(false);
    }
  };

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const response = await weddingEventService.getEvents();
      if (response.success) {
        setEvents(response.data);
      } else {
        setError('Nem sikerült betölteni az eseményeket.');
      }
    } catch (err) {
      console.error('Fetch events error:', err);
      setError('Hiba történt az események betöltése során.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEvent = async (eventData) => {
    try {
      const response = await weddingEventService.createEvent(eventData);
      if (response.success) {
        setCreateDialogOpen(false);
        fetchEvents();
      } else {
        throw new Error(response.message || 'Hiba történt');
      }
    } catch (err) {
      console.error('Create event error:', err);
      throw err;
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Biztosan törölni szeretnéd ezt az eseményt? Ez a művelet visszafordíthatatlan.')) {
      return;
    }
    try {
      const response = await weddingEventService.deleteEvent(eventId);
      if (response.success) {
        fetchEvents();
      } else {
        alert('Nem sikerült törölni az eseményt.');
      }
    } catch (err) {
      console.error('Delete event error:', err);
      alert('Hiba történt az esemény törlése során.');
    }
  };

  const handleMenuOpen = (event, eventData) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setSelectedEvent(eventData);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleEditEvent = async (updateData) => {
    try {
      const response = await weddingEventService.updateEvent(selectedEvent.id, updateData);
      if (response.success) {
        setEditDialogOpen(false);
        setSelectedEvent(null);
        fetchEvents();
      } else {
        throw new Error(response.message || 'Hiba történt');
      }
    } catch (err) {
      console.error('Update event error:', err);
      throw err;
    }
  };

  const getStatusColor = (status) => {
    const colors = { PLANNING: 'warning', CONFIRMED: 'success', COMPLETED: 'default', CANCELLED: 'error' };
    return colors[status] || 'default';
  };

  const getStatusLabel = (status) => {
    const labels = { PLANNING: 'Tervezés alatt', CONFIRMED: 'Megerősítve', COMPLETED: 'Lezajlott', CANCELLED: 'Lemondva' };
    return labels[status] || status;
  };

  // Statisztika kártya komponens
  const StatBox = ({ icon, value, label }) => (
    <Box sx={{ textAlign: 'center', py: 1 }}>
      <Box sx={{ color: 'text.secondary', mb: 0.5 }}>{icon}</Box>
      <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.dark', lineHeight: 1.2 }}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );

  if (loading) {
    return (
      <Box sx={{
        minHeight: '100vh',
        position: 'relative',
        '&::before': {
          content: '""', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundImage: {
            xs: LEAVES_BG_XS,
            md: LEAVES_BG_MD,
          },
          backgroundSize: { xs: '100% auto', md: 'cover' },
          backgroundPosition: { xs: 'center top', md: 'center center' },
          backgroundRepeat: 'no-repeat',
          zIndex: -1,
        },
      }}>
        <Container maxWidth="lg" sx={{ pt: 8, textAlign: 'center' }}>
          <CircularProgress color="primary" />
          <Typography sx={{ mt: 2, color: 'primary.dark' }}>Betöltés...</Typography>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{
      minHeight: '100vh',
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        backgroundImage: {
          xs: LEAVES_BG_XS,
          md: LEAVES_BG_MD,
        },
        backgroundSize: { xs: '100% auto', md: 'cover' },
        backgroundPosition: { xs: 'center top', md: 'center center' },
        backgroundRepeat: 'no-repeat',
        zIndex: -1,
        willChange: 'transform',
        transform: 'translateZ(0)',
      },
    }}>
      <Container maxWidth="lg" sx={{ pt: 4, pb: 6 }}>

        {/* Hero fejléc */}
        <Paper elevation={0} sx={{
          mb: 4,
          p: { xs: 3, md: 4 },
          borderRadius: '20px',
          background: alpha(theme.palette.background.paper, 0.92),
          backdropFilter: 'blur(12px)',
          border: `1px solid ${alpha(theme.palette.secondary.main, 0.30)}`,
          boxShadow: `0 4px 24px ${alpha(theme.palette.primary.dark, 0.10)}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{
              width: 52, height: 52, borderRadius: '14px',
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <FavoriteIcon sx={{ color: '#fff', fontSize: 26 }} />
            </Box>
            <Box>
              <Typography variant="h4" sx={{ color: 'primary.dark', lineHeight: 1.2 }}>
                Esküvő Tervező
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Szervezd meg álmaid esküvőjét egy helyen
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
            size="large"
            sx={{ borderRadius: '12px', px: 3 }}
          >
            Új esemény
          </Button>
        </Paper>

        {/* Hiba */}
        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Ha csak a bemutató van meg: hívjuk fel a figyelmet, hogy a saját esküvőt is létre kell hozni */}
        {events.length > 0 && events.every((e) => e.isDemo) && (
          <Paper elevation={0} sx={{
            mb: 3, p: { xs: 2, sm: 2.5 }, borderRadius: '16px',
            border: `1px solid ${alpha(theme.palette.secondary.main, 0.5)}`,
            bgcolor: alpha(theme.palette.secondary.main, 0.10),
            display: 'flex', alignItems: { xs: 'stretch', sm: 'center' }, gap: 2,
            flexDirection: { xs: 'column', sm: 'row' },
          }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" fontWeight={700} color="primary.dark">
                Nézz körül a bemutató esküvőben!
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Egy kitalált pár kitöltött tervét látod, hogy kipróbálhasd az összes funkciót. Ha készen állsz, hozd létre a saját esküvődet.
              </Typography>
            </Box>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateDialogOpen(true)} sx={{ borderRadius: '12px', flexShrink: 0 }}>
              Saját esküvő létrehozása
            </Button>
          </Paper>
        )}

        {/* Üres állapot */}
        {events.length === 0 ? (
          <Paper elevation={0} sx={{
            textAlign: 'center',
            py: 8, px: 4,
            borderRadius: '20px',
            background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          }}>
            <Box sx={{
              width: 80, height: 80, borderRadius: '20px', mx: 'auto', mb: 3,
              background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.15)} 0%, ${alpha(theme.palette.primary.main, 0.10)} 100%)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <EventIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            </Box>
            <Typography variant="h5" gutterBottom sx={{ color: 'primary.dark' }}>
              Még nincs eseményed
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 400, mx: 'auto' }}>
              Hozd létre az első esküvő eseményedet és kezdj el tervezni!
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
              size="large"
              sx={{ borderRadius: '12px', px: 4 }}
            >
              Esemény létrehozása
            </Button>
          </Paper>
        ) : (
          // MUI v7: a Grid "item" komponens megszűnt, a régi item+xs/sm/lg propok némán
          // figyelmen kívül maradnak — a `size` prop az egyetlen ami tényleg méretezi az
          // oszlopot. Enélkül a Grid item csak a tartalmához igazodó (jellemzően
          // keskenyebb) szélességet vesz fel, nem az oszlop teljes helyét.
          <Grid container spacing={3}>
            {events.map((event) => (
              <Grid key={event.id} size={{ xs: 12, sm: 6, lg: 4 }} sx={{ display: 'flex' }}>
                <Card sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: '20px',
                  border: event.isDemo ? `1px dashed ${theme.palette.secondary.main}` : `1px solid ${theme.palette.divider}`,
                  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                  transition: 'all 0.3s ease',
                  overflow: 'hidden',
                  '&:hover': {
                    transform: 'translateY(-6px)',
                    boxShadow: '0 12px 36px rgba(0,0,0,0.16)',
                    borderColor: theme.palette.secondary.main,
                  },
                }}>
                  {/* Státusz csík a kártya tetején */}
                  <Box sx={{
                    height: 4,
                    background: event.status === 'CONFIRMED'
                      ? `linear-gradient(90deg, ${theme.palette.success.main}, ${theme.palette.primary.main})`
                      : event.status === 'PLANNING'
                      ? `linear-gradient(90deg, ${theme.palette.warning.main}, ${theme.palette.secondary.main})`
                      : event.status === 'CANCELLED'
                      ? theme.palette.error.main
                      : theme.palette.grey[400],
                  }} />

                  <CardActionArea
                    onClick={() => navigate(`/wedding-planner/${event.id}`)}
                    sx={{ flexGrow: 1 }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      {/* Fejléc: név + menü */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                        <Typography variant="h6" component="div" sx={{ flexGrow: 1, color: 'primary.dark', lineHeight: 1.3 }}>
                          {event.name}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, event)}
                          sx={{ ml: 1, flexShrink: 0, color: 'text.secondary' }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Box>

                      {/* Dátum */}
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 0.75 }}>
                        <EventIcon sx={{ color: 'secondary.main', fontSize: 16 }} />
                        <Typography variant="body2" color="text.secondary">
                          {format(new Date(event.eventDate), 'yyyy. MMMM dd.', { locale: hu })}
                        </Typography>
                      </Box>

                      {/* Státusz chip (bemutatónál a „Bemutató” jelölés mellette) */}
                      {event.isDemo && (
                        <Chip label="Bemutató" size="small" sx={{ mb: 2.5, mr: 1, fontWeight: 700, borderRadius: '8px', bgcolor: alpha(theme.palette.secondary.main, 0.25), color: '#7a560f' }} />
                      )}
                      <Chip
                        label={getStatusLabel(event.status)}
                        color={getStatusColor(event.status)}
                        variant="outlined"
                        size="small"
                        sx={{ mb: 2.5, fontWeight: 600, borderRadius: '8px' }}
                      />

                      {/* Elválasztó */}
                      <Box sx={{ height: 1, bgcolor: 'divider', mb: 2 }} />

                      {/* Statisztikák */}
                      <Grid container spacing={1}>
                        <Grid size={4}>
                          <StatBox
                            icon={<PeopleIcon sx={{ fontSize: 18 }} />}
                            value={event._count?.guests || 0}
                            label="Vendég"
                          />
                        </Grid>
                        <Grid size={4}>
                          <StatBox
                            icon={<CheckIcon sx={{ fontSize: 18 }} />}
                            value={event._count?.tasks || 0}
                            label="Feladat"
                          />
                        </Grid>
                        <Grid size={4}>
                          <StatBox
                            icon={<MoneyIcon sx={{ fontSize: 18 }} />}
                            value={formatHufCompact(event.totalBudget)}
                            label="Keret"
                          />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Kontextus menü */}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
          slotProps={{ paper: { sx: { borderRadius: '12px', boxShadow: '0 8px 24px rgba(0,0,0,0.15)' } } }}
        >
          <MenuItem onClick={() => { setEditDialogOpen(true); handleMenuClose(); }}>
            <EditIcon sx={{ mr: 1, fontSize: 18, color: 'primary.main' }} />
            Szerkesztés
          </MenuItem>
          <MenuItem
            onClick={() => { const id = selectedEvent.id; handleMenuClose(); handleDeleteEvent(id); }}
            sx={{ color: 'error.main' }}
          >
            <DeleteIcon sx={{ mr: 1, fontSize: 18 }} />
            Törlés
          </MenuItem>
        </Menu>

        {/* Ha a bemutatót törölte, innen bármikor visszakérheti */}
        {!events.some((e) => e.isDemo) && (
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Button size="small" onClick={handleLoadDemo} disabled={loadingDemo} sx={{ color: 'text.secondary' }}>
              {loadingDemo ? 'Bemutató betöltése…' : 'Bemutató esküvő betöltése — nézd meg, mire képes a tervező'}
            </Button>
          </Box>
        )}

        {/* Létrehozó dialog */}
        <CreateWeddingEventDialog
          open={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
          onSubmit={handleCreateEvent}
        />

        {/* Szerkesztő dialog */}
        <EditWeddingEventDialog
          open={editDialogOpen}
          onClose={() => { setEditDialogOpen(false); setSelectedEvent(null); }}
          onSubmit={handleEditEvent}
          event={selectedEvent}
        />
      </Container>
    </Box>
  );
}

export default WeddingPlannerDashboard;
